import {useInjection} from "@dp-instant/ui";
import {Localize} from "@dp-instant/i18n";
import {GameModel} from "@game/coregame/model/GameModel";
import {TypesGame} from "@game/TypesGame";
import {BackButton} from "@game/ui/components/BackButton/BackButton";
import {CoinsIndicator} from "@game/ui/components/CoinsIndicator/CoinsIndicator";
import {StarsIndicator} from "@game/ui/components/StarsIndicator/StarsIndicator";
import {apiClient} from "@game/api/ApiClient";
import {Images} from "@game/ui/images";
import {playUIClickSound} from "@game/helpers";
import cn from "classnames";
import {useState, useEffect} from "preact/hooks";
import type {ReferralInfo} from "../../../packages/shared/src/types/referral";
import "./ReferralScreen.less";

export const ReferralScreen = () => {
    const model = useInjection<GameModel>(TypesGame.model);
    const [info, setInfo] = useState<ReferralInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        apiClient.trackFunnelEvent("referral_screen_opened");
        apiClient.getReferralInfo()
            .then(data => { setInfo(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const handleClaim = async () => {
        if (claiming || !info || info.unclaimedEarnings <= 0) return;
        playUIClickSound();
        setClaiming(true);
        try {
            const result = await apiClient.claimReferralEarnings();
            apiClient.hardBalance = result.newHardBalance;
            apiClient.emitHardBalanceUpdate();
            setInfo({...info, unclaimedEarnings: 0, totalEarned: info.totalEarned});
        } catch {} finally {
            setClaiming(false);
        }
    };

    const handleCopy = () => {
        if (!info) return;
        playUIClickSound();
        navigator.clipboard?.writeText(info.referralLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleShare = async () => {
        if (!info) return;
        playUIClickSound();
        const tg = (window as any).Telegram?.WebApp;
        const shareText = Localize.defaultLocale?.startsWith("ru")
            ? "Это реально затягивает 😳\nState.io в телеге + тебе дают ⭐ 25 Stars за вход"
            : "This is really addictive 😳\nState.io on Telegram + you get ⭐ 25 Stars on signup";
        if (!tg?.shareMessage) {
            const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(info.referralLink)}&text=${encodeURIComponent(shareText)}`;
            tg?.openTelegramLink?.(shareUrl);
            return;
        }
        try {
            const { preparedMessageId } = await apiClient.prepareReferralShare(Localize.defaultLocale || undefined);
            tg.shareMessage(preparedMessageId, (_sent: boolean) => {});
        } catch {
            const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(info.referralLink)}&text=${encodeURIComponent(shareText)}`;
            tg?.openTelegramLink?.(shareUrl);
        }
    };

    return (
        <div className={cn("screen", "screen__referral")}>
            <div className={cn("screen-top", "visible")}>
                <div className={cn("container", "top-bar")}>
                    <BackButton onClick={() => model.goToLobby()} />
                    <div className="top-bar__currencies">
                        <CoinsIndicator
                            className={cn("coins-indicator", "coins-indicator_filled")}
                            total={model.cookie.coins}
                        />
                        <StarsIndicator
                            className={cn("stars-indicator", "stars-indicator_filled")}
                        />
                    </div>
                </div>
            </div>

            <div className="referral-content">
                {loading ? (
                    <div className="referral-empty">{Localize.get("ui-ref-loading", "Loading...")}</div>
                ) : !info ? (
                    <div className="referral-empty">{Localize.get("ui-ref-error", "Failed to load")}</div>
                ) : (
                    <>
                        {/* Earnings panel */}
                        <div className="referral-unclaimed">
                            <div className="referral-unclaimed__amount">
                                {info.unclaimedEarnings} <Images.Star />
                            </div>
                            <div className="referral-unclaimed__desc">
                                {Localize.get("ui-ref-commission", "Invite friends and earn 20% of their purchases")}
                            </div>
                            {info.unclaimedEarnings > 0 && (
                                <button
                                    className="referral-unclaimed__claim-btn"
                                    onClick={handleClaim}
                                    disabled={claiming}
                                >
                                    {claiming ? "..." : Localize.get("ui-ref-claim", "Claim")}
                                </button>
                            )}
                        </div>

                        {/* Invite buttons */}
                        <div className="referral-invite">
                            <button className="referral-invite__btn referral-invite__copy" onClick={handleCopy}>
                                {copied
                                    ? Localize.get("ui-ref-copied", "Copied!")
                                    : Localize.get("ui-ref-copy", "Copy link")}
                            </button>
                            <button className="referral-invite__btn referral-invite__share" onClick={handleShare}>
                                {Localize.get("ui-ref-share", "Share")}
                            </button>
                        </div>

                        {/* Referral mechanics info */}
                        <div className="referral-info-cards">
                            <div className="referral-info-card">
                                <div className="referral-info-card__icon">🎁</div>
                                <div className="referral-info-card__text">
                                    {Localize.get("ui-ref-friend-bonus", "Your friend gets 25 ⭐ on signup")}
                                </div>
                            </div>
                            <div className="referral-info-card">
                                <div className="referral-info-card__icon">🏆</div>
                                <div className="referral-info-card__text">
                                    {Localize.get("ui-ref-referrer-reward", "You get 10 ⭐ when friend reaches LVL 10")}
                                </div>
                            </div>
                        </div>

                        {/* Friends list */}
                        <div className="referral-friends">
                            <div className="referral-friends__title">
                                {Localize.get("ui-ref-friends", "Friends")} ({info.totalFriends})
                            </div>
                            {info.friends.length === 0 ? (
                                <div className="referral-empty">
                                    {Localize.get("ui-ref-empty", "Invite friends and earn ⭐")}
                                </div>
                            ) : (
                                <div className="referral-friends__list">
                                    {info.friends.map(f => (
                                        <div className="referral-friend" key={f.id}>
                                            {f.photoUrl ? (
                                                <img className="referral-friend__avatar" src={f.photoUrl} />
                                            ) : (
                                                <div className="referral-friend__avatar" />
                                            )}
                                            <div className="referral-friend__info">
                                                <div className="referral-friend__name">
                                                    {f.firstName}
                                                </div>
                                                <div className="referral-friend__level">
                                                    LVL {f.level}
                                                </div>
                                            </div>
                                            {f.earnedForReferrer > 0 && (
                                                <div className="referral-friend__earned">
                                                    +{f.earnedForReferrer} <Images.Star />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
